import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, CheckCircle, XCircle, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = window.location.origin + '/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-[#121214] rounded-2xl p-8 border border-white/5">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Gecersiz Baglanti</h2>
            <p className="text-slate-400 mb-6">Sifre sifirlama baglantisi gecersiz veya eksik. Lutfen tekrar deneyin.</p>
            <Link to="/sifremi-unuttum">
              <Button className="w-full h-12 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold">
                Tekrar Dene
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-[#121214] rounded-2xl p-8 border border-white/5">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Sifre Guncellendi</h2>
            <p className="text-slate-400 mb-6">Sifreniz basariyla guncellendi. Yeni sifrenizle giris yapabilirsiniz.</p>
            <Link to="/giris">
              <Button className="w-full h-12 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold shadow-[0_0_15px_rgba(6,182,212,0.3)]">
                Giris Yap
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error('Lutfen tum alanlari doldurun');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Sifreler eslesmeli');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Sifre en az 6 karakter olmalidir');
      return;
    }

    if (newPassword.length > 72) {
      toast.error('Sifre en fazla 72 karakter olabilir');
      return;
    }

    if (!/[a-zA-Z]/.test(newPassword)) {
      toast.error('Sifre en az bir harf icermelidir');
      return;
    }

    if (!/[0-9]/.test(newPassword)) {
      toast.error('Sifre en az bir rakam icermelidir');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Sifreniz basariyla guncellendi');
    } catch (err) {
      const message = err.response?.data?.detail || 'Bir hata olustu';
      toast.error(message);
      if (message.includes('Gecersiz') || message.includes('suresi dolmus') || message.includes('kullanilmis')) {
        setError(true);
      }
    }
    setLoading(false);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-[#121214] rounded-2xl p-8 border border-white/5">
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Baglanti Gecersiz</h2>
            <p className="text-slate-400 mb-6">Bu sifre sifirlama baglantisi gecersiz, suresi dolmus veya daha once kullanilmis.</p>
            <Link to="/sifremi-unuttum">
              <Button className="w-full h-12 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold">
                Yeni Sifirlama Baglantisi Olustur
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link to="/giris" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" />
          <span>Girise Don</span>
        </Link>

        <div className="bg-[#121214] rounded-2xl p-8 border border-white/5">
          <div className="w-14 h-14 rounded-full bg-cyan-500/10 flex items-center justify-center mx-auto mb-6">
            <KeyRound className="w-7 h-7 text-cyan-400" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-2 text-center">Yeni Sifre Belirle</h1>
          <p className="text-slate-400 text-center mb-8 text-sm">Yeni sifrenizi asagiya girin. Sifre en az 6 karakter, bir harf ve bir rakam icermelidir.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-slate-300">Yeni Sifre</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Yeni sifreniz"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-12 bg-[#1C1C1E] border-white/10 text-white placeholder:text-slate-500 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">Sifre Tekrar</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Sifrenizi tekrar girin"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-12 bg-[#1C1C1E] border-white/10 text-white placeholder:text-slate-500"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold shadow-[0_0_15px_rgba(6,182,212,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.5)] disabled:opacity-50"
            >
              {loading ? (
                <div className="spinner w-5 h-5" />
              ) : (
                'Sifremi Guncelle'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
